import {
  PROFILE_STORES_AI_OFFER_RECOMMENDATION_FEEDBACK,
  PROFILE_STORES_AI_OFFER_RECOMMENDATION_PUBLISH,
  PROFILE_STORES_AI_OFFER_RECOMMENDATION_STATUS,
  PROFILE_STORES_AI_OFFER_RECOMMENDATIONS,
  PROFILE_STORES_AI_OFFER_RULES
} from '../../../shared/constants/endpoints';

const toNumberOrNull = (value) => {
  if (value == null || value === '') {
    return null;
  }
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const trimOrNull = (value) => {
  if (value == null) {
    return null;
  }
  const trimmed = String(value).trim();
  return trimmed || null;
};

const normalizeUpper = (value, fallback = null) => {
  const normalized = trimOrNull(value);
  return normalized ? normalized.toUpperCase() : fallback;
};

export const formatRetryAfter = (totalSeconds) => {
  let seconds = Math.max(1, Math.floor(Number(totalSeconds) || 0));
  const days = Math.floor(seconds / 86400);
  seconds %= 86400;
  const hours = Math.floor(seconds / 3600);
  seconds %= 3600;
  const minutes = Math.floor(seconds / 60);
  seconds %= 60;

  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
};

const normalizeStringArray = (value) => (
  Array.isArray(value)
    ? value.map((item) => String(item).trim()).filter(Boolean)
    : []
);

const unwrapApiData = (payload) => {
  if (payload?.data != null) {
    return payload.data;
  }
  return payload ?? {};
};

export const normalizeAiOfferDraft = (draft = {}) => ({
  ...draft,
  title: trimOrNull(draft?.title) || '',
  description: trimOrNull(draft?.description) || '',
  discount: trimOrNull(draft?.discount) || '',
  mechanicType: normalizeUpper(draft?.mechanicType),
  appliesOnType: normalizeUpper(draft?.appliesOnType, 'STORE_WIDE'),
  appliesOnCategories: trimOrNull(draft?.appliesOnCategories),
  appliesOnProducts: trimOrNull(draft?.appliesOnProducts),
  appliesOnMinCartValue: toNumberOrNull(draft?.appliesOnMinCartValue),
  audience: trimOrNull(draft?.audience),
  channels: normalizeStringArray(draft?.channels),
  validDaysLabel: trimOrNull(draft?.validDaysLabel),
  validityDays: toNumberOrNull(draft?.validityDays),
  redemptionLimitType: normalizeUpper(draft?.redemptionLimitType, 'NONE'),
  redemptionLimitValue: toNumberOrNull(draft?.redemptionLimitValue),
  couponCode: trimOrNull(draft?.couponCode),
  maxDiscountCap: toNumberOrNull(draft?.maxDiscountCap),
  terms: normalizeStringArray(draft?.terms),
  reason: trimOrNull(draft?.reason),
  expectedImpact: trimOrNull(draft?.expectedImpact),
  confidence: toNumberOrNull(draft?.confidence),
  tiersJson: trimOrNull(draft?.tiersJson),
  flatPriceValue: toNumberOrNull(draft?.flatPriceValue),
  freeItemLabel: trimOrNull(draft?.freeItemLabel),
  leadTimeMinHours: toNumberOrNull(draft?.leadTimeMinHours),
  leadTimeMaxHours: toNumberOrNull(draft?.leadTimeMaxHours),
  eligibilityJson: trimOrNull(draft?.eligibilityJson),
  timeWindowsJson: trimOrNull(draft?.timeWindowsJson),
  budgetCeiling: toNumberOrNull(draft?.budgetCeiling),
  minDiscountFloor: toNumberOrNull(draft?.minDiscountFloor),
  stackingMode: normalizeUpper(draft?.stackingMode),
  stackPriority: toNumberOrNull(draft?.stackPriority),
  redemptionMode: normalizeUpper(draft?.redemptionMode)
});

export const normalizeAiOfferRecommendation = (recommendation = {}) => {
  const status = normalizeUpper(recommendation?.status, 'DRAFT');
  return {
    ...recommendation,
    id: recommendation?.id ?? null,
    status,
    sourceType: normalizeUpper(recommendation?.sourceType),
    opportunityType: normalizeUpper(recommendation?.opportunityType),
    model: trimOrNull(recommendation?.model),
    promptVersion: trimOrNull(recommendation?.promptVersion),
    draft: normalizeAiOfferDraft(recommendation?.draft || {}),
    score: toNumberOrNull(recommendation?.score),
    confidence: toNumberOrNull(recommendation?.confidence),
    reasonSummary: trimOrNull(recommendation?.reasonSummary),
    publishedOfferId: recommendation?.publishedOfferId ?? null,
    createdAt: recommendation?.createdAt ?? null,
    updatedAt: recommendation?.updatedAt ?? null,
    isPublished: status === 'PUBLISHED',
    publicationLabel: status === 'PUBLISHED' ? 'Published' : 'Not published'
  };
};

export const normalizeAiOfferRecommendationsPayload = (payload) => {
  const data = unwrapApiData(payload);
  const list = Array.isArray(data)
    ? data
    : Array.isArray(data?.recommendations)
      ? data.recommendations
      : Array.isArray(data?.content)
        ? data.content
        : [];

  return list.map((recommendation) => normalizeAiOfferRecommendation(recommendation));
};

export const normalizeAiOfferRecommendationsEnvelope = (payload) => {
  const data = unwrapApiData(payload);
  return {
    recommendations: normalizeAiOfferRecommendationsPayload(data),
    generationStatus: normalizeUpper(data?.generationStatus),
    generationMessage: trimOrNull(data?.generationMessage),
    generationLimit: toNumberOrNull(data?.generationLimit),
    generationsRemaining: toNumberOrNull(data?.generationsRemaining),
    generationRetryAfterSeconds: toNumberOrNull(data?.generationRetryAfterSeconds)
  };
};

export const normalizeAiOfferRecommendationPayload = (payload) => (
  normalizeAiOfferRecommendation(unwrapApiData(payload))
);

export const normalizeAiOfferRecommendationError = (
  error,
  fallbackMessage = 'Unable to complete AI offer request.'
) => {
  const status = error?.status || error?.response?.status || error?.data?.status || null;
  const responseData = error?.data?.data || error?.response?.data?.data || {};
  const retryAfterSeconds = toNumberOrNull(responseData?.retryAfterSeconds);
  const code = responseData?.code || error?.data?.code || error?.code || null;
  const message = code === 'AI_OFFER_GENERATION_LIMIT' && retryAfterSeconds
    ? `AI offer generation limit reached. You can generate a new set in ${formatRetryAfter(retryAfterSeconds)}.`
    : error?.data?.message || error?.message || fallbackMessage;
  return {
    raw: error,
    status,
    code,
    message,
    retryAfterSeconds,
    retryAt: responseData?.retryAt || null,
    isRateLimited: Number(status) === 429 || /rate|limit|quota/i.test(message)
  };
};

const requireRequest = (request) => {
  if (typeof request !== 'function') {
    throw new Error('Missing authenticated request context');
  }
};

export const listAiOfferRecommendations = async (request, { status } = {}) => {
  requireRequest(request);
  const query = status ? `?status=${encodeURIComponent(status)}` : '';
  const response = await request(`${PROFILE_STORES_AI_OFFER_RECOMMENDATIONS}${query}`, {
    method: 'GET',
    requestName: 'ai-offers-list'
  });
  return normalizeAiOfferRecommendationsEnvelope(response?.data);
};

export const generateAiOfferRecommendations = async (request, payload = {}) => {
  requireRequest(request);
  const response = await request(PROFILE_STORES_AI_OFFER_RECOMMENDATIONS, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    requestName: 'ai-offers-generate',
    body: JSON.stringify(payload || {})
  });
  return normalizeAiOfferRecommendationsEnvelope(response?.data);
};

export const updateAiOfferRecommendationStatus = async (request, recommendationId, payload = {}) => {
  requireRequest(request);
  const response = await request(PROFILE_STORES_AI_OFFER_RECOMMENDATION_STATUS(recommendationId), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    requestName: 'ai-offers-status-update',
    body: JSON.stringify(payload || {})
  });
  return normalizeAiOfferRecommendationPayload(response?.data);
};

export const recordAiOfferRecommendationFeedback = async (request, recommendationId, payload = {}) => {
  requireRequest(request);
  const response = await request(PROFILE_STORES_AI_OFFER_RECOMMENDATION_FEEDBACK(recommendationId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    requestName: 'ai-offers-feedback',
    body: JSON.stringify(payload || {})
  });
  return normalizeAiOfferRecommendationPayload(response?.data);
};

export const normalizeAiOfferRules = (payload) => {
  const data = unwrapApiData(payload);
  return {
    maxDiscountPercent: toNumberOrNull(data?.maxDiscountPercent),
    maxPercentDiscountCapAmount: toNumberOrNull(data?.maxPercentDiscountCapAmount),
    maxFlatDiscount: toNumberOrNull(data?.maxFlatDiscount),
    minBillAmount: toNumberOrNull(data?.minBillAmount)
  };
};

export const getAiOfferRules = async (request) => {
  requireRequest(request);
  const response = await request(PROFILE_STORES_AI_OFFER_RULES, {
    method: 'GET',
    requestName: 'ai-offers-rules-get'
  });
  return normalizeAiOfferRules(response?.data);
};

export const saveAiOfferRules = async (request, payload = {}) => {
  requireRequest(request);
  const body = {
    maxDiscountPercent: toNumberOrNull(payload?.maxDiscountPercent),
    maxPercentDiscountCapAmount: toNumberOrNull(payload?.maxPercentDiscountCapAmount),
    maxFlatDiscount: toNumberOrNull(payload?.maxFlatDiscount),
    minBillAmount: toNumberOrNull(payload?.minBillAmount)
  };
  const response = await request(PROFILE_STORES_AI_OFFER_RULES, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    requestName: 'ai-offers-rules-save',
    body: JSON.stringify(body)
  });
  return normalizeAiOfferRules(response?.data);
};

export const publishAiOfferRecommendation = async (request, recommendationId, payload = {}) => {
  requireRequest(request);
  const response = await request(PROFILE_STORES_AI_OFFER_RECOMMENDATION_PUBLISH(recommendationId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    requestName: 'ai-offers-publish',
    body: JSON.stringify(payload || {})
  });
  return normalizeAiOfferRecommendationPayload(response?.data);
};
